import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { CardContainer } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm, CheckboxInputSM, InputSM, SelectInputSM } from '@apps-common/components/elements'
import { VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { CID_ACCOUNT_REGEX } from '@apps-common/config/regex'
import { useManagementUnitSelectItems } from '@apps-common/hooks/useManagementUnitSelectItems'
import { IUnitCreateMultipleDefaultsModel } from '@apps-common/types/management/unit'

interface IProps {
  defaults?: IUnitCreateMultipleDefaultsModel
  doneCallback: (defaults: IUnitCreateMultipleDefaultsModel) => void
}

export const UnitDefaultsForm: React.FC<IProps> = ({ defaults, doneCallback }) => {
  const { isLoading: selectItemsLoading, companies, products, plans, firmwares, simApns, simTypes } = useManagementUnitSelectItems()
  const [unitDefaults, setUnitDefaults] = useState<IUnitCreateMultipleDefaultsModel>()

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
    reset
  } = useForm<IUnitCreateMultipleDefaultsModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      checkTrafficStream: true,
      autoSave: true
    }
  })

  useEffect(() => {
    if (!selectItemsLoading) {
      setValue('productId', products.defaultValue)
      setValue('unitPlanId', plans.defaultValue)
      setValue('unitFirmwareId', firmwares.defaultValue)
      setValue('simTypeId', simTypes.defaultValue)
    }
  }, [selectItemsLoading])

  useEffect(() => {
    if (defaults) {
      reset(defaults)
    }
  }, [defaults])

  useEffect(() => {
    if (unitDefaults) {
      doneCallback(unitDefaults)
    }
  }, [unitDefaults])

  const submit = (values: IUnitCreateMultipleDefaultsModel) => setUnitDefaults(values)

  const isLoading = selectItemsLoading
  const isUpdating = isSubmitting

  return (
    <form noValidate onSubmit={handleSubmit(submit)}>
      <div className='grid md:grid-cols-2 gap-[20px] max-w-[1200px]'>
        <CardContainer bgClasses='bg-surface border border-border text-text'>
          <SectionTitle text='Company' classes='ml-[10px]' />
          <VertSpacerLg />
          <div className='grid lg:grid-cols-2 gap-x-[20px]'>
            <SelectInputSM
              type='text'
              placeholder='Select Monitoring Centre'
              label='Monitoring Centre'
              id='monitoringCentreId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={companies.defaultValue}
              registration={register('monitoringCentreId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={companies.options}
              errors={errors.monitoringCentreId}
            />
            <SelectInputSM
              type='text'
              placeholder='Select Bureau'
              label='Bureau'
              id='bureauId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={companies.defaultValue}
              registration={register('bureauId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={companies.options}
              errors={errors.bureauId}
            />
            <SelectInputSM
              type='text'
              placeholder='Select Technician'
              label='Technician'
              id='techCompanyId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={companies.defaultValue}
              registration={register('techCompanyId')}
              options={companies.options}
              errors={errors.techCompanyId}
            />
            <div className='lg:col-span-2'>
              <CheckboxInputSM
                label='Invoice on activation'
                id='hardwareToBeInvoiced'
                loading={isLoading}
                disabled={isUpdating}
                registration={register('hardwareToBeInvoiced')}
                errors={errors.hardwareToBeInvoiced}
              />
            </div>
          </div>
        </CardContainer>
        <CardContainer bgClasses='bg-surface border border-border text-text'>
          <SectionTitle text='Device' classes='ml-[10px]' />
          <VertSpacerLg />
          <div className='grid lg:grid-cols-2 gap-x-[20px]'>
            <SelectInputSM
              type='text'
              placeholder='Select Product'
              label='Product'
              id='productId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={products.defaultValue}
              registration={register('productId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={products.options}
              errors={errors.productId}
            />
            <SelectInputSM
              type='text'
              placeholder='Select Plan'
              label='Plan'
              id='unitPlanId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={plans.defaultValue}
              registration={register('unitPlanId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={plans.options}
              errors={errors.unitPlanId}
            />
            <SelectInputSM
              type='text'
              placeholder='Select Firmware'
              label='Firmware'
              id='unitFirmwareId'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              defaultValue={firmwares.defaultValue}
              registration={register('unitFirmwareId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={firmwares.options}
              errors={errors.unitFirmwareId}
            />
            <InputSM
              type='text'
              placeholder='CID account number'
              label='CID account'
              id='accountNo'
              showLabel
              loading={isLoading}
              disabled={isUpdating}
              registration={register('accountNo', {
                pattern: {
                  value: CID_ACCOUNT_REGEX,
                  message: '4 numerical characters only e.g. 0123'
                }
              })}
              errors={errors.accountNo}
            />

            <CheckboxInputSM
              label='Activated'
              id='isActivated'
              loading={isLoading}
              disabled={isUpdating}
              registration={register('isActivated')}
              errors={errors.isActivated}
            />
            <CheckboxInputSM
              label='Line supervision'
              id='lineSupervision'
              loading={isLoading}
              disabled={isUpdating}
              registration={register('lineSupervision')}
              errors={errors.lineSupervision}
            />
          </div>
        </CardContainer>
        <CardContainer bgClasses='bg-surface border border-border text-text'>
          <SectionTitle text='Extras' classes='ml-[10px]' />
          <VertSpacerLg />
          <InputSM
            type='text'
            placeholder='Enter a serial no prefix'
            label='Serial No Prefix'
            id='accountNo'
            info='Prefix to be removed from serial no when scanning them in.'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('serialNoPrefix')}
            errors={errors.serialNoPrefix}
            containerClassName='lg:col-span-2'
          />
          <VertSpacerMd />
          <SelectInputSM
            type='text'
            label='Select Sim Type'
            id='simTypeId'
            showLabel
            className='capitalize'
            loading={isLoading}
            disabled={isUpdating}
            registration={register('simTypeId', {
              required: false
            })}
            options={simTypes.options}
            errors={errors.simTypeId}
          />
        </CardContainer>
        <CardContainer bgClasses='bg-surface border border-border text-text'>
          <SectionTitle text='Processing' classes='ml-[10px]' />
          <VertSpacerLg />
          <CheckboxInputSM
            label='Check traffic stream'
            description='Check for traffic events before saving device.'
            id='checkTrafficStream'
            loading={isLoading}
            disabled={isUpdating}
            registration={register('checkTrafficStream')}
            errors={errors.checkTrafficStream}
          />
          <VertSpacerMd />
          <CheckboxInputSM
            label='Auto save'
            description='Automatically save device after all check are done.'
            id='autoSave'
            loading={isLoading}
            disabled={isUpdating}
            registration={register('autoSave')}
            errors={errors.autoSave}
          />
        </CardContainer>
      </div>

      <VertSpacerLg />
      <ButtonRoundedSm
        type='submit'
        className='action-success'
        loading={isLoading || isUpdating}
        text='Next'
        icon={<commonIcons.arrowRight className='text-[1.1rem]' />}
        iconRight
      />
    </form>
  )
}
